import classNames from "classnames";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { LuCheck, LuChevronDown, LuX } from "react-icons/lu";

import { getInitialsFromFullName } from "../../../constants/constants";
import { useWorkFilterOptions } from "../../../hooks/useWorkFilterOptions";
import { FilterMenu, FilterMenuOption } from "../../works/board/FilterMenu";
import { DATE_PRESETS, DatePreset } from "../reports.utils";
import { useSavedView } from "../saved/SavedViewContext";

const AVATAR_COLORS: Record<string, string> = {
  purple: "#7C3AED",
  blue: "#1E3A8A",
};

export type ReportFilterState = {
  preset: DatePreset;
  userIds: string[];
  clientIds: string[];
  workTypeIds: string[];
};

export const useReportFilters = (
  preset: DatePreset = "thisYear",
  /** Start from these (an AI-made report's saved filters). */
  start?: Partial<Omit<ReportFilterState, "preset">> | null,
) => {
  // A saved standard report ("Save as my report") starts from its saved filters.
  const view = useSavedView();
  const saved = view?.initial?.filters;
  const initial: ReportFilterState = {
    preset: (saved?.preset || preset) as DatePreset,
    userIds: saved?.userIds ?? start?.userIds ?? [],
    clientIds: saved?.clientIds ?? start?.clientIds ?? [],
    workTypeIds: saved?.workTypeIds ?? start?.workTypeIds ?? [],
  };
  const [filters, setState] = useState<ReportFilterState>(initial);
  const setFilters = (patch: Partial<ReportFilterState>) =>
    setState((current) => ({ ...current, ...patch }));
  const clear = () =>
    setState((current) => ({
      ...current,
      userIds: [],
      clientIds: [],
      workTypeIds: [],
    }));
  const anyFilter = !!(
    filters.userIds.length ||
    filters.clientIds.length ||
    filters.workTypeIds.length
  );
  const setViewFilters = view?.setFilters;
  useEffect(() => {
    setViewFilters?.({
      preset: filters.preset,
      userIds: filters.userIds,
      clientIds: filters.clientIds,
      workTypeIds: filters.workTypeIds,
    });
  }, [setViewFilters, filters]);
  return { filters, setFilters, clear, anyFilter };
};

/** Single-choice pill (date range, thresholds…). */
export const ChoiceMenu = <T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);
  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} className={"relative"}>
      <button
        type={"button"}
        aria-haspopup={"listbox"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={
          "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-[#DCE2EA] bg-white px-3 text-[13px] font-semibold text-[#3F4757] hover:border-[#6B4EFF]"
        }>
        {label}: <b className={"text-[#1F2433]"}>{current?.label}</b>
        <LuChevronDown size={14} />
      </button>
      {isOpen && (
        <div
          role={"listbox"}
          className={
            "absolute left-0 top-[42px] z-30 min-w-[190px] rounded-xl border border-[#EDEFF3] bg-white p-1.5 shadow-[0_12px_32px_rgba(20,22,34,0.14)]"
          }>
          {options.map((option) => (
            <button
              key={option.value}
              type={"button"}
              role={"option"}
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={classNames(
                "flex w-full items-center justify-between gap-3 whitespace-nowrap rounded-[7px] px-2.5 py-2 text-left text-[13px] font-semibold hover:bg-[#F5F6F8]",
                option.value === value ? "text-[#6B4EFF]" : "text-[#3A4256]",
              )}>
              {option.label}
              {option.value === value && <LuCheck size={14} strokeWidth={3} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const DateMenu: FC<{
  value: DatePreset;
  onChange: (value: DatePreset) => void;
  label?: string;
}> = ({ value, onChange, label = "Date" }) => (
  <ChoiceMenu
    label={label}
    options={DATE_PRESETS}
    value={value}
    onChange={onChange}
  />
);

/** Team member / client / work type pills (multi-select) + "Clear all". */
export const PeopleClientTypeFilters: FC<{
  state: ReturnType<typeof useReportFilters>;
  showPeople?: boolean;
  showWorkTypes?: boolean;
  peopleLabel?: string;
}> = ({
  state,
  showPeople = true,
  showWorkTypes = true,
  peopleLabel = "Team member",
}) => {
  const [clientSearch, setClientSearch] = useState("");
  const { clientOptions, usersOptions, workTypeOptions } = useWorkFilterOptions(
    {
      searchClients: clientSearch,
      includeUsers: showPeople,
    },
  );

  // Remember client names so picked clients keep their label while searching.
  const [clientNames, setClientNames] = useState<
    Record<string, FilterMenuOption>
  >({});
  const clientMenu = useMemo<FilterMenuOption[]>(
    () =>
      clientOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
        avatar: {
          initials: String(option.avatar || ""),
          color:
            AVATAR_COLORS[String(option.avatarColor)] || AVATAR_COLORS.blue,
          square: option.avatarColor === "purple",
        },
      })),
    [clientOptions],
  );
  useEffect(() => {
    if (!clientMenu.length) return;
    setClientNames((current) => ({
      ...current,
      ...Object.fromEntries(clientMenu.map((o) => [o.value, o])),
    }));
  }, [clientMenu]);
  const clientMenuWithPicked = useMemo(() => {
    const shown = new Set(clientMenu.map((o) => o.value));
    const picked = state.filters.clientIds
      .filter((id) => !shown.has(id) && clientNames[id])
      .map((id) => clientNames[id]);
    return [...picked, ...clientMenu];
  }, [clientMenu, state.filters.clientIds, clientNames]);

  const peopleMenu = useMemo<FilterMenuOption[]>(
    () =>
      usersOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
        avatar: {
          initials:
            String(option.avatar || "") ||
            getInitialsFromFullName(option.label),
          color: option.avatarCustomColor || "#1E3A8A",
        },
      })),
    [usersOptions],
  );
  const workTypeMenu = useMemo<FilterMenuOption[]>(
    () =>
      workTypeOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    [workTypeOptions],
  );

  return (
    <>
      {showPeople && (
        <FilterMenu
          placeholder={peopleLabel}
          options={peopleMenu}
          selected={state.filters.userIds}
          onChange={(userIds) => state.setFilters({ userIds })}
        />
      )}
      <FilterMenu
        placeholder={"Client"}
        options={clientMenuWithPicked}
        selected={state.filters.clientIds}
        onChange={(clientIds) => state.setFilters({ clientIds })}
        search={{ value: clientSearch, onChange: setClientSearch }}
        countNoun={"clients"}
        minWidth={170}
      />
      {showWorkTypes && (
        <FilterMenu
          placeholder={"Work type"}
          options={workTypeMenu}
          selected={state.filters.workTypeIds}
          onChange={(workTypeIds) => state.setFilters({ workTypeIds })}
        />
      )}
      {state.anyFilter && (
        <button
          type={"button"}
          onClick={state.clear}
          className={
            "inline-flex items-center gap-[5px] whitespace-nowrap px-3 py-2 text-[13px] font-semibold text-[#6B4EFF]"
          }>
          <LuX size={14} strokeWidth={2.2} />
          Clear all
        </button>
      )}
    </>
  );
};
